/**
 * FristenPage – Fristkalender mit Ampelsystem
 * Gesetzliche + benutzerdefinierte Fristen
 */
import { useState, useEffect } from 'react';
import { AlertCircle, Clock, CheckCircle, ExternalLink, Plus, Trash2, Check, RefreshCw, HelpCircle, ShieldCheck, History } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import { useTabs } from '@/contexts/TabContext';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const AMPEL = {
  rot: { label: 'Ueberfaellig', bg: 'bg-red-50 border-red-200', text: 'text-red-700', badge: 'bg-red-100 text-red-700', icon: AlertCircle },
  gelb: { label: 'In 30 Tagen', bg: 'bg-amber-50 border-amber-200', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-700', icon: Clock },
  gruen: { label: 'Rechtzeitig', bg: 'bg-green-50 border-green-200', text: 'text-green-700', badge: 'bg-green-100 text-green-700', icon: CheckCircle },
  // Aus Post/KI erkannt, aber noch von niemandem bestaetigt: sichtbar, ohne Ampel und ohne Erinnerung
  // Bestand ohne Bestaetigungsnachweis: Argus ueberwacht sie uebergangsweise weiter, fachlich aber ungeklaert
  altfrist: { label: 'Altfrist – Bestaetigung ungeklaert', bg: 'bg-orange-50 border-orange-300 border-dashed', text: 'text-orange-700', badge: 'bg-orange-100 text-orange-800', icon: History },
  unbestaetigt: { label: 'Pruefung erforderlich', bg: 'bg-slate-50 border-slate-200', text: 'text-slate-500', badge: 'bg-slate-100 text-slate-600', icon: HelpCircle },
};

export default function FristenPage() {
  const [fristen, setFristen] = useState([]);
  const [zusammenfassung, setZusammenfassung] = useState({});
  const [filter, setFilter] = useState('alle');
  const [typFilter, setTypFilter] = useState('alle');
  const [loading, setLoading] = useState(true);
  const [showNewFrist, setShowNewFrist] = useState(false);
  const [klienten, setKlienten] = useState([]);
  const [newFrist, setNewFrist] = useState({ klient_id: '', bezeichnung: '', frist_datum: '', gesetz: '', notiz: '' });
  const [creating, setCreating] = useState(false);
  const { openTab } = useTabs();

  useEffect(() => { loadFristen(); loadKlienten(); }, []);

  const loadFristen = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/fristen`);
      const data = await res.json();
      if (data.success) {
        setFristen(data.fristen || []);
        setZusammenfassung(data.zusammenfassung || {});
      }
    } catch {} finally { setLoading(false); }
  };

  const loadKlienten = async () => {
    try {
      const res = await fetch(`${API_URL}/api/betreuung/klienten`);
      const data = await res.json();
      if (Array.isArray(data)) setKlienten(data);
    } catch {}
  };

  const handleCreateFrist = async () => {
    if (!newFrist.klient_id || !newFrist.bezeichnung || !newFrist.frist_datum) {
      toast.error('Bitte Klient, Bezeichnung und Datum angeben');
      return;
    }
    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/api/fristen/custom`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newFrist),
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Frist erstellt');
        setShowNewFrist(false);
        setNewFrist({ klient_id: '', bezeichnung: '', frist_datum: '', gesetz: '', notiz: '' });
        loadFristen();
      }
    } catch { toast.error('Fehler beim Erstellen'); }
    finally { setCreating(false); }
  };

  const handleMarkErledigt = async (fristId) => {
    try {
      const res = await fetch(`${API_URL}/api/fristen/custom/${fristId}/erledigt?erledigt=true`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        toast.success('Frist als erledigt markiert');
        loadFristen();
      }
    } catch { toast.error('Fehler'); }
  };

  const handleErkannt = async (fristId, aktion) => {
    try {
      const res = await fetch(`${API_URL}/api/fristen/erkannt/${fristId}/${aktion}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}',
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(aktion === 'bestaetigen' ? 'Frist bestaetigt – wird jetzt ueberwacht' : 'Frist als erledigt markiert');
        loadFristen();
      } else {
        toast.error(data.detail || 'Frist konnte nicht gespeichert werden');
      }
    } catch { toast.error('Fehler'); }
  };

  const handleDeleteFrist = async (fristId) => {
    try {
      const res = await fetch(`${API_URL}/api/fristen/custom/${fristId}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        toast.success('Frist geloescht');
        loadFristen();
      }
    } catch { toast.error('Fehler'); }
  };

  const filtered = fristen.filter(f => {
    if (filter !== 'alle' && f.ampel !== filter) return false;
    if (typFilter !== 'alle') {
      if (typFilter === 'custom' && !f.custom) return false;
      if (typFilter === 'erkannt' && !f.erkannt) return false;
      if (typFilter === 'altfrist' && f.einstufung !== 'altfrist') return false;
      if (!['custom', 'erkannt', 'altfrist'].includes(typFilter) && f.typ !== typFilter) return false;
    }
    return true;
  });

  const openAkte = (klientId, klientName) => {
    openTab(`/akten/${klientId}`, { label: klientName, type: 'akte' });
  };

  return (
    <div className="space-y-6" data-testid="fristen-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Fristkalender</h1>
          <p className="text-slate-500 mt-1">Gesetzliche und individuelle Fristen aller Betreuungsakten</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadFristen} data-testid="fristen-refresh">
            <RefreshCw className="w-4 h-4 mr-2" /> Aktualisieren
          </Button>
          <Button onClick={() => setShowNewFrist(true)} data-testid="neue-frist-btn">
            <Plus className="w-4 h-4 mr-2" /> Neue Frist
          </Button>
        </div>
      </div>

      {/* Neue Frist Dialog */}
      {showNewFrist && (
        <div className="bg-white border-2 border-violet-200 rounded-xl p-5 shadow-lg" data-testid="neue-frist-form">
          <h3 className="text-base font-semibold text-slate-800 mb-4">Neue Frist erstellen</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Klient</Label>
              <Select value={newFrist.klient_id} onValueChange={v => setNewFrist(p => ({ ...p, klient_id: v }))}>
                <SelectTrigger data-testid="frist-klient-select"><SelectValue placeholder="Klient waehlen..." /></SelectTrigger>
                <SelectContent>
                  {klienten.slice(0, 50).map(k => (
                    <SelectItem key={k.id} value={k.id}>{k.vorname} {k.nachname}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Bezeichnung</Label>
              <Input value={newFrist.bezeichnung} onChange={e => setNewFrist(p => ({ ...p, bezeichnung: e.target.value }))}
                placeholder="z.B. Genehmigung Wohnungsaufloesung" data-testid="frist-bezeichnung" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Frist-Datum</Label>
              <Input type="date" value={newFrist.frist_datum} onChange={e => setNewFrist(p => ({ ...p, frist_datum: e.target.value }))}
                data-testid="frist-datum" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Gesetzesreferenz (optional)</Label>
              <Input value={newFrist.gesetz} onChange={e => setNewFrist(p => ({ ...p, gesetz: e.target.value }))}
                placeholder="z.B. § 1851 BGB" data-testid="frist-gesetz" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs">Notiz (optional)</Label>
              <Textarea value={newFrist.notiz} onChange={e => setNewFrist(p => ({ ...p, notiz: e.target.value }))}
                placeholder="Zusaetzliche Informationen..." rows={2} data-testid="frist-notiz" />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowNewFrist(false)}>Abbrechen</Button>
            <Button onClick={handleCreateFrist} disabled={creating} data-testid="frist-speichern-btn">
              {creating ? 'Speichern...' : 'Frist speichern'}
            </Button>
          </div>
        </div>
      )}

      {/* Ampel-Uebersicht */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {(['rot', 'gelb', 'gruen', 'altfrist', 'unbestaetigt']).map(key => {
          const conf = AMPEL[key];
          const Icon = conf.icon;
          const count = zusammenfassung[key] || 0;
          return (
            <button key={key} onClick={() => setFilter(filter === key ? 'alle' : key)}
              className={`p-4 rounded-xl border-2 transition-all ${conf.bg} ${
                filter === key ? 'ring-2 ring-offset-1 ring-violet-400' : ''
              }`} data-testid={`ampel-${key}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon className={`w-5 h-5 ${conf.text}`} />
                  <span className={`text-sm font-semibold ${conf.text}`}>{conf.label}</span>
                </div>
                <span className={`text-2xl font-bold ${conf.text}`}>{count}</span>
              </div>
            </button>
          );
        })}
      </div>

      {(zusammenfassung.vorschlaege_offen > 0 || zusammenfassung.ohne_datum > 0) && (
        <p className="text-xs text-slate-500" data-testid="fristen-hinweis">
          {zusammenfassung.vorschlaege_offen > 0 && `${zusammenfassung.vorschlaege_offen} Fristvorschlaege warten in der Eingangspruefung. `}
          {zusammenfassung.ohne_datum > 0 && `${zusammenfassung.ohne_datum} erkannte Fristen ohne eindeutiges Datum (in der Akte pruefen).`}
        </p>
      )}

      {/* Typ-Filter */}
      <div className="flex gap-2 flex-wrap">
        {[
          { key: 'alle', label: 'Alle Typen' },
          { key: 'jahresbericht', label: 'Jahresbericht' },
          { key: 'rechnungslegung', label: 'Rechnungslegung' },
          { key: 'gerichtsueberpruefung', label: 'Gerichtsueberpruefung' },
          { key: 'verguetungsantrag', label: 'Verguetungsantrag' },
          { key: 'custom', label: 'Individuelle Fristen' },
          { key: 'erkannt', label: 'Aus Post erkannt' },
          { key: 'altfrist', label: 'Altfristen (ungeklaert)' },
        ].map(t => (
          <Button key={t.key} size="sm"
            variant={typFilter === t.key ? 'default' : 'outline'}
            onClick={() => setTypFilter(t.key)}
            className="text-xs" data-testid={`typ-filter-${t.key}`}>
            {t.label}
          </Button>
        ))}
      </div>

      {/* Fristen-Liste */}
      <div className="space-y-2">
        {loading ? (
          <div className="text-center py-8 text-slate-400">Lade Fristen...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-8 text-slate-400">Keine Fristen fuer diesen Filter</div>
        ) : filtered.map(f => {
          const conf = AMPEL[f.ampel];
          const Icon = conf.icon;
          return (
            <div key={f.id}
              className={`flex items-center gap-4 p-3 rounded-lg border ${conf.bg} hover:shadow-sm transition-shadow`}
              data-testid={`frist-${f.id}`}>
              <Icon className={`w-5 h-5 ${conf.text} shrink-0`} />
              <div className="flex-1 min-w-0 cursor-pointer" onClick={() => openAkte(f.klient_id, f.klient_name)}>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-slate-800">{f.klient_name}</span>
                  {f.aktenzeichen && <Badge variant="outline" className="text-[10px]">{f.aktenzeichen}</Badge>}
                  {f.custom && <Badge className="text-[10px] bg-violet-100 text-violet-700">Individuell</Badge>}
                  {f.erkannt && (f.bestaetigt
                    ? <Badge className="text-[10px] bg-sky-100 text-sky-700">Aus Post erkannt</Badge>
                    : f.einstufung === 'altfrist'
                      ? <Badge className="text-[10px] bg-orange-100 text-orange-800" data-testid={`frist-altfrist-${f.id}`}
                          title="Bestand ohne Bestaetigungsnachweis: wird uebergangsweise weiter ueberwacht, ist aber fachlich nicht bestaetigt">
                          Altfrist – Bestaetigung ungeklaert</Badge>
                      : <Badge className="text-[10px] bg-slate-200 text-slate-700">Pruefung erforderlich – noch nicht ueberwacht</Badge>)}
                  {f.freigabe?.art === 'automatisch' && (
                    <Badge className="text-[10px] bg-amber-100 text-amber-800" title={f.freigabe.grund || ''} data-testid={`frist-auto-${f.id}`}>
                      Automatisch freigegeben – nicht menschlich bestaetigt</Badge>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs text-slate-600">{f.typ_label}</span>
                  {f.gesetz && <span className="text-[10px] text-slate-400">{f.gesetz}</span>}
                  {f.notiz && <span className="text-[10px] text-slate-400 italic">- {f.notiz}</span>}
                </div>
              </div>
              <div className="text-right shrink-0">
                <p className={`text-sm font-bold ${conf.text}`}>
                  {f.ampel === 'unbestaetigt' ? 'Pruefung erforderlich' :
                   f.ampel === 'altfrist' ? (f.tage_bis < 0 ? `${Math.abs(f.tage_bis)} Tage ueberfaellig` : `${f.tage_bis} Tage`) :
                   f.tage_bis < 0 ? `${Math.abs(f.tage_bis)} Tage ueberfaellig` :
                   f.tage_bis === 0 ? 'Heute faellig' :
                   `${f.tage_bis} Tage`}
                </p>
                <p className="text-[10px] text-slate-400">
                  {f.frist_datum ? new Date(f.frist_datum).toLocaleDateString('de-DE') : ''}
                </p>
              </div>
              {f.custom && (
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => handleMarkErledigt(f.id)}
                    className="p-1.5 rounded-lg hover:bg-green-100 text-green-600 transition-colors"
                    title="Als erledigt markieren" data-testid={`frist-erledigt-${f.id}`}>
                    <Check className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDeleteFrist(f.id)}
                    className="p-1.5 rounded-lg hover:bg-red-100 text-red-500 transition-colors"
                    title="Loeschen" data-testid={`frist-delete-${f.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
              {f.erkannt && (
                <div className="flex gap-1 shrink-0">
                  {!f.bestaetigt && (
                    <button onClick={() => handleErkannt(f.id, 'bestaetigen')}
                      className="p-1.5 rounded-lg hover:bg-sky-100 text-sky-600 transition-colors"
                      title="Frist bestaetigen (danach Ampel, Erinnerung und Ueberwachung)" data-testid={`frist-bestaetigen-${f.id}`}>
                      <ShieldCheck className="w-4 h-4" />
                    </button>
                  )}
                  <button onClick={() => handleErkannt(f.id, 'erledigt')}
                    className="p-1.5 rounded-lg hover:bg-green-100 text-green-600 transition-colors"
                    title="Als erledigt markieren" data-testid={`frist-erledigt-${f.id}`}>
                    <Check className="w-4 h-4" />
                  </button>
                </div>
              )}
              {!f.custom && (
                <ExternalLink className="w-4 h-4 text-slate-300 shrink-0 cursor-pointer"
                  onClick={() => openAkte(f.klient_id, f.klient_name)} />
              )}
            </div>
          );
        })}
      </div>

      <div className="text-center text-xs text-slate-400 pt-4">
        {filtered.length} von {fristen.length} Fristen angezeigt
      </div>
    </div>
  );
}
